import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

const EVENT = 'pebble-montessori-changed';

export function useMontessoriMode() {
  const [enabled, setEnabled] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user ?? null;
      if (cancelled) return;
      if (!user) { setLoading(false); return; }
      setUserId(user.id);
      const { data } = await supabase
        .from('profiles')
        .select('montessori_mode')
        .eq('id', user.id)
        .maybeSingle();
      if (cancelled) return;
      setEnabled(!!data?.montessori_mode);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Sync across multiple hook instances (e.g., Profile + ThemeProvider).
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ enabled: boolean }>).detail;
      if (detail) setEnabled(detail.enabled);
    };
    window.addEventListener(EVENT, handler);
    return () => window.removeEventListener(EVENT, handler);
  }, []);

  const setMode = useCallback(async (value: boolean) => {
    if (!userId) return { ok: false as const, error: 'Not signed in' };
    const prev = enabled;
    setEnabled(value);
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { enabled: value } }));
    const { error } = await supabase
      .from('profiles')
      .update({ montessori_mode: value })
      .eq('id', userId);
    if (error) {
      setEnabled(prev);
      window.dispatchEvent(new CustomEvent(EVENT, { detail: { enabled: prev } }));
      return { ok: false as const, error: error.message };
    }
    return { ok: true as const };
  }, [userId, enabled]);

  return { enabled, setMode, loading, userId };
}
